import { rem } from 'polished'
import { useCallback } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEndPlaceIsSame from './Hooks/useEndPlaceIsSame'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateCruiseItemViewModel } from '../Create/models'
import type { PatchCruiseItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const StyledVerticalSpacer = styled(VerticalSpacer)`
  margin-top: ${rem(20)};
  margin-bottom: ${rem(20)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const EndPlaceContainer = styled.div`
  padding-bottom: ${rem(8)};
`

const DividerContainer = styled.div`
  height: ${rem(24)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

interface Props {
  tripId: string
}

function CruiseFields({ tripId }: Props) {
  const { required, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateCruiseItemViewModel | PatchCruiseItemViewModel
  >()
  const { watch, control, setValue, register, clearErrors } = methods

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  useEndPlaceIsSame(methods)

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(
        `${placeName}.name`,
        selectedOption.details?.name || selectedOption.label,
      )

      setValue(
        `${placeName}.type`,
        selectedOption.prediction ? 'GEO' : 'CUSTOM',
      )

      if (selectedOption.prediction) {
        const { place_id } = selectedOption.prediction
        setValue(`${placeName}.id`, place_id)
      }

      if (selectedOption.details) {
        const { geometry, formatted_address, website, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        if (website) {
          setValue('url', website)
        }
        formatted_address && setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const {
    fieldState: { error: endPlaceError },
  } = useController({ name: 'endPlace' })

  const handleSetStartPlaceToEndPlace = useCallback(() => {
    setValue('endPlace', startPlace)

    // React hook form not recognizing valid field after this update
    if (endPlaceError) {
      clearErrors('endPlace')
    }
  }, [setValue, startPlace, clearErrors, endPlaceError])

  const cruiseNameLabel = intl.formatMessage({
    id: '92o4ivsg2szPQ7oepsSK',
    defaultMessage: 'Cruise name',
    description: 'Form label for cruise name field in cruise trip item editor',
  })

  const cruiseNamePlaceholder = intl.formatMessage({
    id: '-2MVd14x_kTLJHCo7V-m',
    defaultMessage: 'Enter a cruise name',
    description: 'Placeholder text for cruise name input in cruise trip item editor',
  })

  const pickUpDetailsHeading = intl.formatMessage({
    id: 'DkbJurFAU0a5SH4AA0qO',
    defaultMessage: 'Pick up details',
    description: 'Section heading for pick up details in cruise trip item editor',
  })

  const pickUpLocationLabel = intl.formatMessage({
    id: 'uYiPd-Enfg7OrynwYuaA',
    defaultMessage: 'Pick up location',
    description: 'Form label for pick up location field in cruise trip item editor',
  })

  const searchLocationPlaceholder = intl.formatMessage({
    id: 'HMLSeigW89CQOv6znkaC',
    defaultMessage: 'Search for location',
    description: 'Placeholder text for location search input in cruise trip item editor',
  })

  const addressLabel = intl.formatMessage({
    id: 'yW9IRY2eULB_RS__Lk5Y',
    defaultMessage: 'Address',
    description: 'Form field label for address display in cruise trip item',
  })

  const pickUpDateLabel = intl.formatMessage({
    id: '8QRXLtpWQWSSwF777D0A',
    defaultMessage: 'Pick up date',
    description: 'Form label for pick up date field in cruise trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'juMRCbVWPfRs1IJUuVvI',
    defaultMessage: 'Time',
    description: 'Form label for time field in cruise trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'KVLV_sFdSAA7YQ6u4Z4m',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in cruise trip item editor',
  })

  const dropOffDetailsHeading = intl.formatMessage({
    id: 'K2mpPifeEgq5xGcfir_r',
    defaultMessage: 'Drop off details',
    description: 'Section heading for drop off details in cruise trip item editor',
  })

  const dropOffLocationLabel = intl.formatMessage({
    id: 'NPog8WiqhxwRNnLsI8-V',
    defaultMessage: 'Drop-off location',
    description: 'Form label for drop-off location field in cruise trip item editor',
  })

  const dropOffDateLabel = intl.formatMessage({
    id: 'f7KqLOppfoO1oLis7ieT',
    defaultMessage: 'Drop-off date',
    description: 'Form label for drop-off date field in cruise trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'awnUORkRQEvEHltDnDtJ',
    defaultMessage: 'Operator',
    description: 'Form label for cruise operator field in cruise trip item editor',
  })

  const operatorPlaceholder = intl.formatMessage({
    id: '462aDrpmMZ8R7kZj0aN_',
    defaultMessage: 'Cruise operator',
    description: 'Placeholder text for cruise operator input in cruise trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'nqjh3JCgkfUjcyEFNUFO',
    defaultMessage: 'Price',
    description: 'Form label for price field in cruise trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'pnYPvIazwMyJrZf6JtXV',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in cruise trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: '7SusKKWZSVg6WxGStFiu',
    defaultMessage: 'Note',
    description: 'Form label for notes field in cruise trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: '11royVeeZsJKv8-4LbuQ',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field in the pricing or cost breakdown section',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'YJDWIzhAMQIYT78QXghI',
    defaultMessage: 'https://',
    description: 'Input placeholder text for website URL input field',
  })

  const notePlaceholder = intl.formatMessage({
    id: '8lL-HnK60ahgBfwDB2Vn',
    defaultMessage: 'Add note here',
    description: 'Input placeholder text for notes textarea field in the trip planner item editor modal',
  })

  const copyAddressHelpText = <BodyText variant="medium">
    <FormattedMessage
      id="MdTov-xZWkgEc2jZkpPC"
      defaultMessage="Add same address as <link>pick up location</link>"
      description="Link to copy pick up address to drop off address in cruise form"
      values={{
        link: (chunks) => <TextLink onClick={handleSetStartPlaceToEndPlace} underline={false}>{chunks}</TextLink>,
      }}
    />
  </BodyText>

  return (
    <StyledVerticalSpacer gap={16}>
      <Input
        {...register('name', { required })}
        required
        label={cruiseNameLabel}
        placeholder={cruiseNamePlaceholder}
      />

      <Heading variant="heading4">{pickUpDetailsHeading}</Heading>

      <TripAddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label={pickUpLocationLabel}
        placeholder={searchLocationPlaceholder}
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        startIcon={<LineMapMarkerIcon />}
      />
      {startPlace?.address && (
        <AddressDisplay title={addressLabel} value={startPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={pickUpDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{dropOffDetailsHeading}</Heading>

      <EndPlaceContainer>
        <TripAddressAutocomplete
          {...register('endPlace.name', { required })}
          required
          label={dropOffLocationLabel}
          placeholder={searchLocationPlaceholder}
          initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          details={ADDRESS_RESPONSE_DETAILS}
          onPlaceSelect={handleEndAddressChange}
          startIcon={<LineMapMarkerIcon />}
          helpText={copyAddressHelpText}
        />
        {endPlaceError && copyAddressHelpText}
      </EndPlaceContainer>

      {endPlace?.address && (
        <AddressDisplay title={addressLabel} value={endPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="endDate"
          label={dropOffDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <Input
          {...register('operatorName')}
          label={operatorLabel}
          placeholder={operatorPlaceholder}
        />

        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notePlaceholder}
          label={noteLabel}
          size="tall"
          resize="vertical"
        />
      </ShowMore>
    </StyledVerticalSpacer>
  )
}

export default CruiseFields
